import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import FullFinancialReview from "../../assets/FullFinancialReview/accessfinances.png";
import OptimizeInvestmentImg from "../../assets/FullFinancialReview/optimiseinvestments.png";
import RetirementplanImg from "../../assets/FullFinancialReview/retirementplan.png";
import FinancialHealthImg from "../../assets/FullFinancialReview/financialhealth.png";
import PersonalisedstratergyImg from "../../assets/FullFinancialReview/personalisedstratergy.png";
import SmartInvestmentImg from "../../assets/FullFinancialReview/smartinvestment.png";
import Retirementplan from "../../assets/FullFinancialReview/RetirementWealthProtection.png";
import { Helmet } from "react-helmet-async";
import "./FinancialReview.css";

const FinancialReview: React.FC = () => {
  const financialInfo = {
    "Assess My Finances": {
      description:
        "Get a clear understanding of your current financial situation with a personalised assessment. Our experts analyse your income, expenses, and savings to help you make informed decisions.",
      img: FullFinancialReview,
    },
    "Optimise My Investments": {
      description:
        "Maximise your wealth with tailored investment strategies. Our team provides insights on risk management, market trends, and asset allocation to ensure financial growth.",
      img: OptimizeInvestmentImg,
    },
    "Plan for Retirement": {
      description:
        "Ensure a secure future with our retirement planning services. We help you develop a savings strategy that aligns with your financial goals and lifestyle needs.",
      img: RetirementplanImg,
    },
    "Improve My Financial Health": {
      description:
        "Strengthen your financial foundation with expert guidance. We provide solutions for debt management, tax planning, and long-term financial stability.",
      img: FinancialHealthImg,
    },
  };

  const navigate = useNavigate();

  const handleRedirect = () => {
    navigate("/contact/");
    setTimeout(() => {
      const element = document.getElementById("get-in-touch");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
      },
    },
  };

  const fadeInVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.8 } },
  };

  const [heroRef, heroInView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  return (
    <div className="financial-review">
      <Helmet>
        <title>
          Full Financial Review - Apollo Financial Services | Apollo Financial
          Services
        </title>
        <meta
          name="description"
          content="Get a full financial review from Apollo Financial Services. Expert analysis of your investments, retirement planning, and financial health in Ireland."
        />
        <meta
          name="keywords"
          content="how to choose a financial advisor in Ireland, best retirement planning services near me, what does a financial planner do, benefits of hiring a fiduciary advisor, top investment strategies for 2025, how to plan for early retirement, estate planning tips for Irish residents, tax-efficient investment options, saving for children's education in Ireland, managing debt effectively, financial planning for first-time homebuyers, how to create a personal budget, tips for reducing personal debt, investment options for beginners, how to build an emergency fund, retirement planning for self-employed individuals, understanding pension schemes in Ireland, how to maximize tax refunds, strategies for wealth accumulation, financial planning for single parents, how to protect assets from inflation, estate planning for blended families, how to minimize inheritance taxes, financial planning after job loss, managing finances during economic downturns, how to diversify investment portfolios, tips for saving for a wedding, financial planning for new parents, how to plan for children's education expenses, strategies for paying off student loans, how to improve credit scores, financial planning for expatriates returning to Ireland, how to invest in sustainable funds, understanding risk tolerance in investments, how to set financial goals, tips for managing monthly expenses, how to prepare for financial emergencies, retirement planning for public sector employees, how to choose the right insurance policies, financial planning for caregivers, Apollo Financial Services, compare financial advisors in Ireland, top financial planning firms, best wealth management companies, independent vs. fiduciary advisors, Apollo Financial reviews, Apollo Financial services offered, Apollo Financial contact information, Apollo Financial client testimonials, Apollo Financial office locations."
        />
        <meta
          property="og:title"
          content="Full Financial Review - Apollo Financial Services"
        />
        <meta
          property="og:description"
          content="Gain clarity on your financial future with Apollo's expert investment strategies, retirement planning, and financial health assessments."
        />
        <meta
          property="og:url"
          content="https://www.apollofinancial.ie/services/financial-review/"
        />
        <meta property="og:type" content="website" />
        <link
          rel="canonical"
          href="https://www.apollofinancial.ie/services/financial-review/"
        />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FinancialService",
            name: "Apollo Financial Services",
            description: "Professional financial review services in Ireland",
            url: "https://www.apollofinancial.ie/services/financial-review/",
            serviceType: "Full Financial Review",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Thomastown House, Thomastown",
              addressLocality: "Duleek",
              addressRegion: "Co. Meath",
              postalCode: "A92 X362",
              addressCountry: "IE",
            },
            offers: {
              "@type": "Offer",
              name: "Full Financial Review",
              description:
                "Full analysis of your financial situation including investments, retirement planning, and tax optimization",
              url: "https://www.apollofinancial.ie/services/financial-review/",
            },
            areaServed: {
              "@type": "GeoCircle",
              geoMidpoint: {
                "@type": "GeoCoordinates",
                latitude: 53.5144115,
                longitude: -6.4057979,
              },
              geoRadius: 50000,
            },
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://www.apollofinancial.ie/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Financial Review",
                item: "https://www.apollofinancial.ie/services/financial-review/",
              },
            ],
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <motion.section
        ref={heroRef}
        className="financial-review-hero"
        initial="hidden"
        animate={heroInView ? "visible" : "hidden"}
        variants={fadeInVariants}
      >
        <div className="hero-overlay"></div>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={heroInView ? "visible" : "hidden"}
        >
          <motion.h1 variants={itemVariants}>Full Financial Review</motion.h1>
          <motion.p variants={itemVariants}>
            Gain clarity and control over your financial future with expert
            guidance tailored to your needs.
          </motion.p>
        </motion.div>
      </motion.section>

      {/* Call to Action Section */}
      <div className="financial-review-actions">
        {Object.entries(financialInfo).map(
          ([title, { description, img }], index) => {
            return (
              <div
                className={`financial-review-section ${
                  index % 2 === 0 ? "text-left" : "text-right"
                }`}
                key={title}
              >
                <div className="financial-review-content">
                  <h2>{title}</h2>
                  <p>{description}</p>
                  <button
                    className="financial-review-btn"
                    onClick={handleRedirect}
                  >
                    Find Out More →
                  </button>
                </div>
                <div className="financial-review-image">
                  <img src={img} alt={title} />
                </div>
              </div>
            );
          }
        )}
      </div>

      {/* Why Financial Review Matters Section */}
      <section className="financial-review-content-section">
        <div className="financial-review-intro">
          <h2>Why a Financial Review Matters</h2>
          <p>
            Our comprehensive financial review service helps you build a strong
            financial future. From investment planning to tax optimisation, we
            provide the expertise you need to grow and protect your wealth.
          </p>
        </div>

        <div className="financial-review-services">
          <div className="financial-review-service-item">
            <img
              src={PersonalisedstratergyImg}
              alt="Wealth Strategy"
              className="financial-review-service-image"
            />
            <div className="financial-review-service-content">
              <h3>Personalised Wealth Strategy</h3>
              <p>
                ✔ Develop a financial plan that aligns with your short and
                long-term goals.
              </p>
            </div>
          </div>

          <div className="financial-review-service-item financial-review-reverse">
            <img
              src={SmartInvestmentImg}
              alt="Investment Planning"
              className="financial-review-service-image"
            />
            <div className="financial-review-service-content">
              <h3>Smart Investment Planning</h3>
              <p>
                ✔ Receive expert insights on investment strategies tailored to
                your risk profile.
              </p>
            </div>
          </div>

          <div className="financial-review-service-item">
            <img
              src={Retirementplan}
              alt="Retirement Planning"
              className="financial-review-service-image"
            />
            <div className="financial-review-service-content">
              <h3>Retirement & Wealth Protection</h3>
              <p>
                ✔ Plan for the future with secure and tax-efficient retirement
                strategies.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="financial-review-why">
        <h2>Why Choose Our Financial Review Services?</h2>
        <div className="financial-review-features">
          <div className="financial-review-feature-item">
            <h3>Expert Guidance</h3>
            <p>
              Work with top financial advisors to craft a plan tailored to your
              needs.
            </p>
          </div>
          <div className="financial-review-feature-item">
            <h3>Tax Optimisation</h3>
            <p>Reduce liabilities and maximise your financial savings.</p>
          </div>
          <div className="financial-review-feature-item">
            <h3>Long-Term Stability</h3>
            <p>
              Ensure your finances are structured for lasting growth and
              security.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FinancialReview;
